import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { LatLonPoint } from '@/types/barriers';
import { ROUTE_VARIANT_KEYS, type RouteVariantKey } from '@/types/routes';

export interface RouteDraftEndpoint {
  readonly lat: number;
  readonly lon: number;
  readonly address: string | null;
}

export interface RouteDraft {
  readonly origin: RouteDraftEndpoint | null;
  readonly destination: RouteDraftEndpoint | null;
}

/**
 * Per-route toggle state for which variants are rendered on the map.
 *
 * The default (returned by `getVariantToggles`) is "show every
 * variant". Individual routes can opt specific variants out via
 * `setVariantToggle` — the table renders one checkbox per variant per
 * row. Entries are only materialised when the user deviates from the
 * "all on" default, keeping memory linear in the visible-route count.
 */
export type RouteVariantToggles = Readonly<Record<RouteVariantKey, boolean>>;

interface RouteWorkspaceValue {
  readonly draft: RouteDraft | null;

  /** Routes rendered on the map as GeoJSON layers. */
  readonly visibleRouteIds: ReadonlySet<string>;
  /** Focused route: drives right-panel details and map pan/zoom. */
  readonly focusedRouteId: string | null;
  /** Controls variant visibility per route; missing key = all-on default. */
  readonly variantToggles: ReadonlyMap<string, RouteVariantToggles>;

  readonly startDraft: () => void;
  readonly setOrigin: (endpoint: RouteDraftEndpoint) => void;
  readonly setDestination: (endpoint: RouteDraftEndpoint) => void;
  readonly clearOrigin: () => void;
  readonly clearDestination: () => void;
  readonly cancelDraft: () => void;

  readonly setVisibleRouteIds: (ids: Iterable<string>) => void;
  readonly addVisibleRoute: (id: string) => void;
  readonly removeVisibleRoute: (id: string) => void;
  readonly clearVisibleRoutes: () => void;

  readonly focusRoute: (id: string | null) => void;

  readonly getVariantToggles: (id: string) => RouteVariantToggles;
  readonly setVariantToggle: (
    id: string, variant: RouteVariantKey, visible: boolean,
  ) => void;

  /** Back-compat: single-route selection used by the right sidebar. */
  readonly selectedRouteId: string | null;
  readonly selectRoute: (id: string | null) => void;
}

const RouteWorkspaceContext = createContext<RouteWorkspaceValue | undefined>(
  undefined,
);

const EMPTY_DRAFT: RouteDraft = { origin: null, destination: null };

const ALL_ON: RouteVariantToggles = Object.freeze(
  ROUTE_VARIANT_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {} as Record<RouteVariantKey, boolean>),
);

interface RouteWorkspaceProviderProps {
  readonly children: ReactNode;
}

export function RouteWorkspaceProvider({ children }: RouteWorkspaceProviderProps) {
  const [draft, setDraft] = useState<RouteDraft | null>(null);
  const [visibleRouteIds, setVisibleIdsState] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [focusedRouteId, setFocusedRouteId] = useState<string | null>(null);
  const [variantToggles, setVariantTogglesState] = useState<
    ReadonlyMap<string, RouteVariantToggles>
  >(new Map());

  const startDraft = useCallback(() => {
    setFocusedRouteId(null);
    setDraft(EMPTY_DRAFT);
  }, []);

  const setOrigin = useCallback((endpoint: RouteDraftEndpoint) => {
    setDraft((prev) => (prev ? { ...prev, origin: endpoint } : prev));
  }, []);

  const setDestination = useCallback((endpoint: RouteDraftEndpoint) => {
    setDraft((prev) => (prev ? { ...prev, destination: endpoint } : prev));
  }, []);

  const clearOrigin = useCallback(() => {
    setDraft((prev) => (prev ? { ...prev, origin: null } : prev));
  }, []);

  const clearDestination = useCallback(() => {
    setDraft((prev) => (prev ? { ...prev, destination: null } : prev));
  }, []);

  const cancelDraft = useCallback(() => {
    setDraft(null);
  }, []);

  const setVisibleRouteIds = useCallback((ids: Iterable<string>) => {
    const next = new Set(ids);
    setVisibleIdsState(next);
    // If the focused route drops out of the visible set (e.g. the user
    // unchecked it in the table), clear the focus so the right panel
    // doesn't keep showing details for a route that is no longer on
    // the map.
    setFocusedRouteId((current) =>
      current !== null && !next.has(current) ? null : current,
    );
  }, []);

  const addVisibleRoute = useCallback((id: string) => {
    setVisibleIdsState((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const removeVisibleRoute = useCallback((id: string) => {
    setVisibleIdsState((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setVariantTogglesState((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    setFocusedRouteId((current) => (current === id ? null : current));
  }, []);

  const clearVisibleRoutes = useCallback(() => {
    setVisibleIdsState(new Set());
    setVariantTogglesState(new Map());
    setFocusedRouteId(null);
  }, []);

  const focusRoute = useCallback((id: string | null) => {
    setDraft(null);
    setFocusedRouteId(id);
    if (id !== null) {
      setVisibleIdsState((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    }
  }, []);

  const getVariantToggles = useCallback(
    (id: string): RouteVariantToggles => variantToggles.get(id) ?? ALL_ON,
    [variantToggles],
  );

  const setVariantToggle = useCallback(
    (id: string, variant: RouteVariantKey, visible: boolean) => {
      setVariantTogglesState((prev) => {
        const current = prev.get(id) ?? ALL_ON;
        if (current[variant] === visible) return prev;
        const next = new Map(prev);
        next.set(id, { ...current, [variant]: visible });
        return next;
      });
    },
    [],
  );

  // Back-compat single-route API: maps to focusedRouteId.
  const selectRoute = useCallback(
    (id: string | null) => {
      setDraft(null);
      if (id === null) {
        setFocusedRouteId(null);
        return;
      }
      focusRoute(id);
    },
    [focusRoute],
  );

  const value = useMemo<RouteWorkspaceValue>(
    () => ({
      draft,
      visibleRouteIds,
      focusedRouteId,
      variantToggles,
      startDraft,
      setOrigin,
      setDestination,
      clearOrigin,
      clearDestination,
      cancelDraft,
      setVisibleRouteIds,
      addVisibleRoute,
      removeVisibleRoute,
      clearVisibleRoutes,
      focusRoute,
      getVariantToggles,
      setVariantToggle,
      selectedRouteId: focusedRouteId,
      selectRoute,
    }),
    [
      draft,
      visibleRouteIds,
      focusedRouteId,
      variantToggles,
      startDraft,
      setOrigin,
      setDestination,
      clearOrigin,
      clearDestination,
      cancelDraft,
      setVisibleRouteIds,
      addVisibleRoute,
      removeVisibleRoute,
      clearVisibleRoutes,
      focusRoute,
      getVariantToggles,
      setVariantToggle,
      selectRoute,
    ],
  );

  return (
    <RouteWorkspaceContext.Provider value={value}>
      {children}
    </RouteWorkspaceContext.Provider>
  );
}

export function useRouteWorkspace(): RouteWorkspaceValue {
  const ctx = useContext(RouteWorkspaceContext);
  if (!ctx) {
    throw new Error('useRouteWorkspace must be used within RouteWorkspaceProvider');
  }
  return ctx;
}

export function latLonFromEndpoint(e: RouteDraftEndpoint): LatLonPoint {
  return { lat: e.lat, lon: e.lon };
}
